import { alpha, Box, styled, SxProps, Theme, Typography } from '@mui/material';
import { ReactNode } from 'react';

export type StandaloneBadgeSize = 'medium' | 'small' | 'chip';

const SMALL_BADGE_HEIGHT = '1.0625rem';
const CHIP_TINT_OPACITY = 0.07;

const StandaloneBadgeContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'size',
})<{ size: StandaloneBadgeSize }>(({ theme, size }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: '0 0 auto',
  minWidth: '1.5rem',
  height: '1.25rem',
  padding: `0 ${theme.spacing(1)}`,
  backgroundColor: theme.palette.custom.OV.sys.surfaceVariant,
  color: theme.palette.custom.OV.sys.onSurface,
  borderRadius: '6.25rem',
  ...(size === 'small' && {
    minWidth: 0,
    height: SMALL_BADGE_HEIGHT,
    padding: '0 0.375rem',
    backgroundColor: theme.palette.custom.OV.sys.surfaceContainerHigh,
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
  }),
  ...(size === 'chip' && {
    minWidth: 0,
    height: 'auto',
    padding: '0.1875rem 0.5rem',
    backgroundColor: alpha(theme.palette.custom.OV.sys.onSurface, CHIP_TINT_OPACITY),
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
  }),
}));

/** `small` is the quieter count that sits beside a section heading. */
export const StandaloneBadge = ({
  badgeContent,
  size = 'medium',
  sx,
}: {
  badgeContent: ReactNode;
  size?: StandaloneBadgeSize;
  sx?: SxProps<Theme>;
}) => {
  return (
    <StandaloneBadgeContainer size={size} sx={sx}>
      {size === 'small' && (
        <Typography
          component="span"
          fontSize="0.6875rem"
          fontWeight={600}
          lineHeight={SMALL_BADGE_HEIGHT}
        >
          {badgeContent}
        </Typography>
      )}
      {size === 'chip' && (
        <Typography
          component="span"
          fontSize="0.65625rem"
          fontWeight={500}
          lineHeight={1.2}
          letterSpacing="0.01em"
        >
          {badgeContent}
        </Typography>
      )}
      {size === 'medium' && (
        <Typography variant="body1" fontSize="small" component="span">
          {badgeContent}
        </Typography>
      )}
    </StandaloneBadgeContainer>
  );
};
